import React, { useState } from "react";
import { Users, Trash2, ChevronRight } from "react-feather";
import { useTheme } from "../../theme/ThemeContext";
import { Card, SectionTitle, Sheet, Button, Field, useInputStyle } from "../ui";

const GroupSheet = ({ groupId, groupsApi, onClose }) => {
  const { tokens } = useTheme();
  const input = useInputStyle();
  const group = groupsApi.groups.find((g) => g.id === groupId);
  const [name, setName] = useState(group ? group.name : "");
  const [query, setQuery] = useState("");
  const [found, setFound] = useState(undefined); // undefined = not searched, null = nobody
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!group) return null;

  const run = async (fn) => {
    setBusy(true);
    setMessage("");
    try {
      await fn();
    } catch (err) {
      setMessage(err.message);
    }
    setBusy(false);
  };

  const search = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setFound(undefined);
    run(async () => setFound(await groupsApi.search(query.trim())));
  };

  const add = () =>
    run(async () => {
      await groupsApi.addMember(group.id, found.email || found.username);
      setFound(undefined);
      setQuery("");
    });

  // A group someone else made and added me to: I can see who is in it, or leave.
  if (!group.isOwner) {
    return (
      <Sheet title={group.name} onClose={onClose}>
        <p style={{ margin: "0 0 12px", fontSize: 13, color: tokens.textMuted }}>
          Created by {group.ownerName}. You can share tasks with the people in this group.
        </p>
        {group.members.map((m, i) => (
          <div key={m.userId} style={{ padding: "10px 0", borderTop: i ? `1px solid ${tokens.surfaceBorder}` : "none", fontSize: 15, fontWeight: 600, color: tokens.textPrimary }}>
            {m.username}
            {m.username === group.ownerName && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: tokens.accent }}>OWNER</span>}
          </div>
        ))}
        {message && <div role="alert" style={{ margin: "10px 0", fontSize: 13, color: tokens.negative }}>{message}</div>}
        <Button kind="danger" disabled={busy} style={{ width: "100%", marginTop: 16 }} onClick={() => run(async () => { await groupsApi.leave(group.id); onClose(); })}>
          Leave group
        </Button>
      </Sheet>
    );
  }

  return (
    <Sheet title="Group" onClose={onClose}>
      <Field label="Group name">
        <div style={{ display: "flex", gap: 8 }}>
          <input style={input} value={name} onChange={(e) => setName(e.target.value)} maxLength={50} />
          <Button kind="ghost" disabled={busy || !name.trim() || name.trim() === group.name} onClick={() => run(() => groupsApi.rename(group.id, name))} style={{ padding: "0 16px" }}>Save</Button>
        </div>
      </Field>

      <div style={{ margin: "8px 0 8px", fontSize: 13, fontWeight: 700, color: tokens.textPrimary }}>People ({group.members.length})</div>
      {group.members.length === 0 && <p style={{ margin: "0 0 12px", fontSize: 13, color: tokens.textMuted }}>No one yet. Search for someone below.</p>}
      {group.members.map((m) => (
        <div key={m.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: `1px solid ${tokens.surfaceBorder}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: tokens.textPrimary }}>{m.username}</div>
            {m.email && <div style={{ fontSize: 12, color: tokens.textMuted, overflow: "hidden", textOverflow: "ellipsis" }}>{m.email}</div>}
          </div>
          <button onClick={() => run(() => groupsApi.removeMember(group.id, m.userId))} aria-label={`Remove ${m.username}`} style={{ background: "none", border: "none", padding: 8, cursor: "pointer", color: tokens.negative }}>
            <Trash2 size={18} />
          </button>
        </div>
      ))}

      <form onSubmit={search} style={{ marginTop: 16 }}>
        <Field label="Find someone by their email or username">
          <div style={{ display: "flex", gap: 8 }}>
            <input style={input} value={query} onChange={(e) => { setQuery(e.target.value); setFound(undefined); }} placeholder="name@example.com" autoCapitalize="none" autoCorrect="off" inputMode="email" />
            <Button type="submit" kind="ghost" disabled={busy || !query.trim()} style={{ padding: "0 16px" }}>Search</Button>
          </div>
        </Field>
      </form>
      <p style={{ margin: "0 0 10px", fontSize: 12, color: tokens.textMuted }}>Type the full email or username. Partial matches aren't shown.</p>

      {found === null && <div style={{ marginBottom: 10, fontSize: 13, color: tokens.textMuted }}>No one found with that email or username.</div>}
      {found && (
        <Card style={{ padding: 12, marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: tokens.textPrimary }}>{found.username}</div>
            <div style={{ fontSize: 12, color: tokens.textMuted }}>{found.email}</div>
          </div>
          {group.members.some((m) => m.username === found.username) ? (
            <span style={{ fontSize: 12, color: tokens.textMuted }}>Already in group</span>
          ) : (
            <Button disabled={busy} onClick={add} style={{ padding: "8px 16px", fontSize: 14 }}>Add</Button>
          )}
        </Card>
      )}

      {message && <div role="alert" style={{ marginBottom: 10, fontSize: 13, color: tokens.negative }}>{message}</div>}

      {!confirmDelete ? (
        <Button kind="danger" style={{ width: "100%", marginTop: 8 }} onClick={() => setConfirmDelete(true)}>Delete group</Button>
      ) : (
        <Button kind="danger" style={{ width: "100%", marginTop: 8 }} disabled={busy} onClick={() => run(async () => { await groupsApi.remove(group.id); onClose(); })}>
          Tap again to delete this group
        </Button>
      )}
      <p style={{ margin: "10px 0 0", fontSize: 12, color: tokens.textMuted }}>Deleting a group or removing someone doesn't remove them from tasks that are already shared.</p>
    </Sheet>
  );
};

const GroupsSection = ({ groupsApi }) => {
  const { tokens } = useTheme();
  const input = useInputStyle();
  const [name, setName] = useState("");
  const [openId, setOpenId] = useState(null);
  const [error, setError] = useState("");

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError("");
    try {
      await groupsApi.create(name.trim());
      setName("");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <SectionTitle>Groups</SectionTitle>
      <Card style={{ padding: "4px 16px 16px" }}>
        {groupsApi.groups.length === 0 && (
          <p style={{ fontSize: 14, color: tokens.textMuted }}>Make a group of people you share tasks with, such as family or a team.</p>
        )}
        {groupsApi.groups.map((g, i) => (
          <button
            key={g.id}
            onClick={() => setOpenId(g.id)}
            style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 0", background: "none", border: "none", borderTop: i ? `1px solid ${tokens.surfaceBorder}` : "none", textAlign: "left", cursor: "pointer", color: tokens.textPrimary }}
          >
            <Users size={18} color={tokens.accent} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{g.name}</div>
              <div style={{ fontSize: 12, color: tokens.textMuted, marginTop: 2 }}>{g.isOwner ? `${g.members.length} ${g.members.length === 1 ? "person" : "people"}` : `Created by ${g.ownerName} · ${g.members.length} others`}</div>
            </div>
            <ChevronRight size={18} color={tokens.textMuted} />
          </button>
        ))}

        <form onSubmit={create} style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="New group name" maxLength={50} />
          <Button type="submit" disabled={!name.trim()} style={{ padding: "0 18px" }}>Create</Button>
        </form>
        {error && <div role="alert" style={{ marginTop: 8, fontSize: 13, color: tokens.negative }}>{error}</div>}
      </Card>

      {openId && <GroupSheet groupId={openId} groupsApi={groupsApi} onClose={() => setOpenId(null)} />}
    </>
  );
};

export default GroupsSection;
